import { useMemo } from "react";
import {
  useGetOEMProcesses,
  useCreateOEMProcess,
  useUpdateOEMProcess,
  useGetOEMPartProcesses,
  useCreateOEMPartProcess,
  useUpdateOEMPartProcess,
  useGetOEMPartOtherProcesses,
  useCreateOEMPartOtherProcess,
  useUpdateOEMPartOtherProcess,
  useGetSpareProcesses,
  useCreateSpareProcess,
  useUpdateSpareProcess,
  useGetSparePartProcesses,
  useCreateSparePartProcess,
  useUpdateSparePartProcess,
  useGetSparePartOtherProcesses,
  useCreateSparePartOtherProcess,
  useUpdateSparePartOtherProcess,
  useGetMaterialOtherProcesses,
  useCreateMaterialOtherProcess,
  useUpdateMaterialOtherProcess,
  useGetMaterial2ndTierProcesses,
  useCreateMaterial2ndTierProcess,
  useUpdateMaterial2ndTierProcess,
  useGetChildPartProcesses,
  useCreateChildPartProcess,
  useUpdateChildPartProcess,
} from "@/hooks/masterHook";
import type { Master } from "@/api/masterService";

const processMasterTabs = [
  { label: "OEM", value: 0 },
  { label: "OEM PART", value: 1 },
  { label: "OEM PART OTHER", value: 2 },
  { label: "SPARE", value: 3 },
  { label: "SPARE PART", value: 4 },
  { label: "SPARE PART OTHER", value: 5 },
  { label: "MATERIAL OTHER", value: 6 },
  { label: "MATERIAL 2ND TIER", value: 7 },
  { label: "CHILD PART", value: 8 },
];

export const useProcessMasterData = (tabValue: number) => {
  const {
    data: oemData,
    isLoading: isLoadingOEM,
    isError: isErrorOEM,
  } = useGetOEMProcesses({}, tabValue === 0);

  const {
    data: oemPartData,
    isLoading: isLoadingOEMPart,
    isError: isErrorOEMPart,
  } = useGetOEMPartProcesses({}, tabValue === 1);

  const {
    data: oemPartOtherData,
    isLoading: isLoadingOEMPartOther,
    isError: isErrorOEMPartOther,
  } = useGetOEMPartOtherProcesses({}, tabValue === 2);

  const {
    data: spareData,
    isLoading: isLoadingSpare,
    isError: isErrorSpare,
  } = useGetSpareProcesses({}, tabValue === 3);

  const {
    data: sparePartData,
    isLoading: isLoadingSparePart,
    isError: isErrorSparePart,
  } = useGetSparePartProcesses({}, tabValue === 4);

  const {
    data: sparePartOtherData,
    isLoading: isLoadingSparePartOther,
    isError: isErrorSparePartOther,
  } = useGetSparePartOtherProcesses({}, tabValue === 5);

  const {
    data: materialOtherData,
    isLoading: isLoadingMaterialOther,
    isError: isErrorMaterialOther,
  } = useGetMaterialOtherProcesses({}, tabValue === 6);

  const {
    data: material2ndTierData,
    isLoading: isLoadingMaterial2ndTier,
    isError: isErrorMaterial2ndTier,
  } = useGetMaterial2ndTierProcesses({}, tabValue === 7);

  const {
    data: childPartData,
    isLoading: isLoadingChildPart,
    isError: isErrorChildPart,
  } = useGetChildPartProcesses({}, tabValue === 8);

  const rows = useMemo(() => {
    const dataMap: { [key: number]: Master[] } = {
      0: oemData || [],
      1: oemPartData || [],
      2: oemPartOtherData || [],
      3: spareData || [],
      4: sparePartData || [],
      5: sparePartOtherData || [],
      6: materialOtherData || [],
      7: material2ndTierData || [],
      8: childPartData || [],
    };

    const sorted = [...(dataMap[tabValue] || [])].sort((a, b) => {
        if (a.is_active === b.is_active) {
          return a.id - b.id;
        }
        return a.is_active ? -1 : 1;
    });
    return sorted;
  }, [
    tabValue,
    oemData,
    oemPartData,
    oemPartOtherData,
    spareData,
    sparePartData,
    sparePartOtherData,
    materialOtherData,
    material2ndTierData,
    childPartData,
  ]);

  const isLoading = useMemo(() => {
    switch (tabValue) {
      case 0: return isLoadingOEM;
      case 1: return isLoadingOEMPart;
      case 2: return isLoadingOEMPartOther;
      case 3: return isLoadingSpare;
      case 4: return isLoadingSparePart;
      case 5: return isLoadingSparePartOther;
      case 6: return isLoadingMaterialOther;
      case 7: return isLoadingMaterial2ndTier;
      case 8: return isLoadingChildPart;
      default: return false;
    }
  }, [
    isLoadingChildPart,
    isLoadingMaterial2ndTier,
    isLoadingMaterialOther,
    isLoadingOEMPartOther,
    isLoadingOEMPart,
    isLoadingOEM,
    isLoadingSparePartOther,
    isLoadingSparePart,
    isLoadingSpare,
    tabValue,
  ]);

  const isError = useMemo(() => {
    switch (tabValue) {
      case 0: return isErrorOEM;
      case 1: return isErrorOEMPart;
      case 2: return isErrorOEMPartOther;
      case 3: return isErrorSpare;
      case 4: return isErrorSparePart;
      case 5: return isErrorSparePartOther;
      case 6: return isErrorMaterialOther;
      case 7: return isErrorMaterial2ndTier;
      case 8: return isErrorChildPart;
      default: return false;
    }
  }, [
    isErrorChildPart,
    isErrorMaterial2ndTier,
    isErrorMaterialOther,
    isErrorOEMPartOther,
    isErrorOEMPart,
    isErrorOEM,
    isErrorSparePartOther,
    isErrorSparePart,
    isErrorSpare,
    tabValue,
  ]);

  const countActive = useMemo(
    () => rows.filter((row: Master) => row.is_active).length,
    [rows]
  );

  const countInactive = useMemo(
    () => rows.filter((row: Master) => !row.is_active).length,
    [rows]
  );

  const createOEMProcess = useCreateOEMProcess();
  const updateOEMProcess = useUpdateOEMProcess();
  const createOEMPartProcess = useCreateOEMPartProcess();
  const updateOEMPartProcess = useUpdateOEMPartProcess();
  const createOEMPartOtherProcess = useCreateOEMPartOtherProcess();
  const updateOEMPartOtherProcess = useUpdateOEMPartOtherProcess();
  const createSpareProcess = useCreateSpareProcess();
  const updateSpareProcess = useUpdateSpareProcess();
  const createSparePartProcess = useCreateSparePartProcess();
  const updateSparePartProcess = useUpdateSparePartProcess();
  const createSparePartOtherProcess = useCreateSparePartOtherProcess();
  const updateSparePartOtherProcess = useUpdateSparePartOtherProcess();
  const createMaterialOtherProcess = useCreateMaterialOtherProcess();
  const updateMaterialOtherProcess = useUpdateMaterialOtherProcess();
  const createMaterial2ndTierProcess = useCreateMaterial2ndTierProcess();
  const updateMaterial2ndTierProcess = useUpdateMaterial2ndTierProcess();
  const createChildPartProcess = useCreateChildPartProcess();
  const updateChildPartProcess = useUpdateChildPartProcess();

  const createProcess = useMemo(() => {
    switch (tabValue) {
      case 0:
        return createOEMProcess;
      case 1:
        return createOEMPartProcess;
      case 2:
        return createOEMPartOtherProcess;
      case 3:
        return createSpareProcess;
      case 4:
        return createSparePartProcess;
      case 5:
        return createSparePartOtherProcess;
      case 6:
        return createMaterialOtherProcess;
      case 7:
        return createMaterial2ndTierProcess;
      case 8:
        return createChildPartProcess;
    }
  }, [
    createChildPartProcess,
    createMaterial2ndTierProcess,
    createMaterialOtherProcess,
    createOEMPartOtherProcess,
    createOEMPartProcess,
    createOEMProcess,
    createSparePartOtherProcess,
    createSparePartProcess,
    createSpareProcess,
    tabValue,
  ]);

  const updateProcess = useMemo(() => {
    switch (tabValue) {
      case 0:
        return updateOEMProcess;
      case 1:
        return updateOEMPartProcess;
      case 2:
        return updateOEMPartOtherProcess;
      case 3:
        return updateSpareProcess;
      case 4:
        return updateSparePartProcess;
      case 5:
        return updateSparePartOtherProcess;
      case 6:
        return updateMaterialOtherProcess;
      case 7:
        return updateMaterial2ndTierProcess;
      case 8:
        return updateChildPartProcess;
    }
  }, [
    tabValue,
    updateOEMProcess,
    updateOEMPartProcess,
    updateOEMPartOtherProcess,
    updateSpareProcess,
    updateSparePartProcess,
    updateSparePartOtherProcess,
    updateMaterialOtherProcess,
    updateMaterial2ndTierProcess,
    updateChildPartProcess,
  ]);

  return {
    rows,
    isLoading,
    isError,
    countActive,
    countInactive,
    processMasterTabs,
    createProcess,
    updateProcess,
  };
};
